/* eslint-disable max-lines-per-function */
import React from "react";
import { navigate, RouteComponentProps } from "@reach/router";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { Button, Card, CardContent } from "@material-ui/core";
import moment, { Moment } from "moment";
import { gql } from "graphql-tag";
import { useMutation } from "@apollo/client";
import { Form, Formik, FormikHelpers } from "formik";
import * as y from "yup";
import { useSnackbar } from "components/haven-design/snackbar";
import logger from "services/logger";

const CREATE_MONTHLY_BILLING = gql`
  mutation createMonthlyBillings($month: Date!) {
    createMonthlyBillings(month: $month)
  }
`;

const navigateToMonth = (month?: Date | Moment | null) => {
  navigate(`/dash/admin/billing/new/${moment(month).startOf("month").format("MM-YYYY")}`, { replace: true });
};

const formSchema = y.object().shape({
  month: y.date().required().max(moment().startOf("month").add(-1, "month")),
});

interface InitialValues {
  month: Moment | null;
}

interface BillingNewProps extends RouteComponentProps
{
  month?: string;
}
const NewBillingPage: React.FC<BillingNewProps> = ({ month: monthParam }) => {
  const { openSnackbar } = useSnackbar();

  const [createMonthlyBillings] = useMutation(CREATE_MONTHLY_BILLING, {
    refetchQueries: ["invoicedTrackingQueries"],
  });

  const handleSubmit = ({ month }:InitialValues, actions:FormikHelpers<InitialValues>) => {
    if (!month) {
      actions.setSubmitting(false);
      return;
    }

    createMonthlyBillings({
      variables: { month: month.format("YYYY-MM-DD") },
    })
      .then(() => {
        openSnackbar({ message: "Successfully created monthly billings.", status: "success" });
        navigate(`/dash/admin/billing/${moment(month).startOf("month").format("MM-YYYY")}`);
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: err.message, status: "error" });
      });
  };

  const initialValues:InitialValues = {
    month: moment(monthParam, "MM-YYYY").startOf("month"),
  };

  return (
    <Card>
      <CardContent>
        <Formik
          initialValues={initialValues}
          validationSchema={formSchema}
          validateOnMount
          onSubmit={(values, actions) => handleSubmit(values, actions)}
        >
          {({ values: { month }, setFieldValue, validateField, isValid }) => (
            <Form>
              <h1>Create Monthly Billing</h1>
              <KeyboardDatePicker
                label="Month"
                value={month}
                format="MM/yyyy"
                openTo="month"
                autoOk
                disableFuture
                maxDate={moment().startOf("month").add(-1, "month")}
                maxDateMessage="The selected date must be before the current month."
                views={["year", "month"]}
                onChange={(date) => {
                  if (date) {
                    navigateToMonth(date);
                    setFieldValue("month", moment(date).startOf("month"), true);
                    setTimeout(() => validateField("month"), 10);
                  }
                }} />
              <Button
                style={{ marginLeft: 16 }}
                variant="contained"
                color="primary"
                disabled={!isValid}
                type="submit">
          Create Monthly Billing
              </Button>
            </Form>
          )}
        </Formik>
      </CardContent>
      <CardContent>
        <p><em>
            This will create monthly billing records for
            each enabled organization or property.
        </em></p>
      </CardContent>
    </Card>
  );
};

export default NewBillingPage;
